export const MOBILE_MAX_WIDTH = 768;
export const MOBILE_FIXED_WIDTH = 450;
export const HEADER_HEIGHT = 64;
export const FOOTER_HEIGHT = 70;
export const PAGE_PADDING = 24;
export const BOTTOM_BTN_HEIGHT = 80;

// 공통코드 - 부모코드명
export const COMMON_CODE_NAME = {
  ACNT_STTS_CD: 'ACNT_STTS_CD',
  GENDER_TYPE_CD: 'GENDER_TYPE_CD',
  USER_TYPE_CD: 'USER_TYPE_CD',
  TERMS_TYPE_CD: 'TERMS_TYPE_CD',
  HSPTL_CD: 'HSPTL_CD',
  MED_QST_TYPE_CD: 'MED_QST_TYPE_CD',
  MEDICINE_CD: 'MEDICINE_CD',
  ALARM_TYPE_CD: 'ALARM_TYPE_CD',
  MISSION_TYPE_CD: 'MISSION_TYPE_CD',
  FTND_RESULT_CD: 'FTND_RESULT_CD',
  CRAVING_TYPE_CD: 'CRAVING_TYPE_CD',
  FAQ_TYPE_CD: 'FAQ_TYPE_CD',
  OS_TYPE_CD: 'OS_TYPE_CD',
  FILTER_TYPE_CD: 'FILTER_TYPE_CD',
};

// 갈망 원인 카테고리 부모 코드
export const CRAVING_CATEGORY_PID = {
  SITUATION: 5900001,
  LOCATION: 5900002,
  WITH_WHOM: 5900003,
  EMOTION: 5900004,
} as const;

export type CravingCategoryPid = typeof CRAVING_CATEGORY_PID[keyof typeof CRAVING_CATEGORY_PID];

// 약관 유형 코드
export const TERMS_TYPE_CODE = {
  SERVICE: 4000001,
  PRIVACY_POLICY: 4000002,
  REGISTER: 4000003,
  MARKETING: 4000004,
};

// 사용자 유형 코드
export const USER_TYPE_CD = {
  USER: 1007001,
  ADMIN: 1007002,
  MEDICALSTAFF: 1007003,
};

// 챗봇 코드
export const CHATBOT_TYPE = {
  SPEAKER: {
    CHATBOT: 'chatbot',
    USER: 'user',
  },
  MISSION_CONV_TYPE: {
    MESSAGE: 'message',
    QUESTION: 'question',
    QUESTIONS: 'questions',
    END: 'end',
    RAG: 'RAG',
    CARD: 'card',
    PIECHART: 'piechart',
    SOS: 'sos',
    IMAGE: 'image',
    USER: 'user',
    HISTORY: 'history_',
    HISTORY_MESSAGE: 'history_message',
    HISTORY_USER: 'history_user',
    HISTORY_IMAGE: 'history_image',
    HISTORY_PIECHART: 'history_piechart',
  },
  USER_OPTION: {
    BUTTON: 'button',
    NAVIGATE_BUTTON: 'navigate_button', // 클릭 시 페이지 이동 버튼 (contentLinks)
    CHECKBOX: 'checkbox',
    TEXT: 'text',
    SLIDER: 'slider',
    CHECKBOX_: 'checkbox_', // 최소 선택 개수가 정해진 체크박스
  },
};

// 알림 유형 코드
export const ALARM_TYPE_CD = {
  MEDICINE: 5600001,
  MISSION: 5600002,
  DIARY: 5600003,
  CRAVING_REMINDER: 5600004,
  PATTERN_ANALYSIS: 5600005,
  CRAVING_HIGH: 5600006,
  CRAVING_MEDIUM: 5600007,
  CRAVING_LOW: 5600008,
};

// 문진 유형 코드
export const MED_QST_TYPE_CD = {
  BASICQUESTIONNAIRE: 5100001,
  DEMOGRAPHICINFORMATION: 5100002,
  SMOKINGINFORMATION: 5100003,
  QSU: 5100004,
  KSCMS: 5100005,
};

// 문진 - 흡연 유형 선택지 SID (medQstChoiceSid 기준)
// 흡연정보 섹션의 첫 질문(medQstSid=10) 선택지들과 매핑됨
export const SMOKING_TYPE_CHOICE_SID = {
  REGULAR_CIGARETTE: 31, // 일반 궐련 담배
  HEATED_TOBACCO: 154, // 궐련형 전자담배
  LIQUID_VAPE: 155, // 액상형 전자담배
} as const;

// 흡연 유형별 조건부 질문 개수 (medQstChoiceSid → 파생 질문 수)
// 흡연 유형 선택 시 추가로 노출되는 질문의 수
export const SMOKING_TYPE_QUESTION_COUNT: Record<number, number> = {
  [SMOKING_TYPE_CHOICE_SID.REGULAR_CIGARETTE]: 2, // 개비 수 + 시작 나이
  [SMOKING_TYPE_CHOICE_SID.HEATED_TOBACCO]: 2, // 개비 수 + 시작 나이
  [SMOKING_TYPE_CHOICE_SID.LIQUID_VAPE]: 3, // 회수 + 회당 분 + 시작 나이
};

// 갈망원인 유형 코드
// 백엔드 NicocareCodes enum과 코드 1:1 매칭. 색은 각 SVG 아이콘 배경색과 일치.
export const CRAVING_TYPE_CD = {
  // Situation (pcode 5900001)
  DRINKING_OCCASIONS: { code: 5900001002, color: '#05529F' }, // 술자리
  AFTER_MEALS: { code: 5900001003, color: '#FFA127' }, // 식사 후
  COFFEE: { code: 5900001004, color: '#7B3507' }, // 커피 마실 때
  BREAK_TIME: { code: 5900001005, color: '#FD4583' }, // 쉬는 시간
  AROUND_SMOKERS: { code: 5900001006, color: '#1A808C' }, // 흡연자 주변
  UPON_WAKING_UP: { code: 5900001007, color: '#F9CA32' }, // 기상 후
  BEFORE_BED: { code: 5900001008, color: '#88238B' }, // 취침 전
  COMMUTE: { code: 5900001009, color: '#28B252' }, // 출/퇴근
  WHEN_BORED: { code: 5900001010, color: '#554AEE' }, // 지루함
  ETC_SITUATION: { code: 5900001999, color: '#98A2B3' }, // 기타

  // Location (pcode 5900002)
  HOME_VERANDA: { code: 5900002001, color: '#3491F5' }, // 집/베란다
  BAR_PUB: { code: 5900002002, color: '#D154B8' }, // 술집/바
  CAFE: { code: 5900002003, color: '#AF6833' }, // 카페
  NEAR_WORK: { code: 5900002004, color: '#3831A1' }, // 직장 근처
  PARK_OUTDOOR: { code: 5900002005, color: '#9CB95D' }, // 야외/공원
  IN_CAR: { code: 5900002006, color: '#4CCFF0' }, // 차 안
  ETC_LOCATION: { code: 5900002999, color: '#98A2B3' }, // 기타

  // WithWhom (pcode 5900003)
  ALONE: { code: 5900003001, color: '#A379E9' }, // 혼자
  FRIEND: { code: 5900003002, color: '#F36A71' }, // 친구
  COWORKER: { code: 5900003003, color: '#02BBD9' }, // 회사 동료
  FAMILY: { code: 5900003004, color: '#DD316E' }, // 가족
  LOVER: { code: 5900003005, color: '#FD4583' }, // 연인
  ETC_WITHWHOM: { code: 5900003999, color: '#98A2B3' }, // 기타

  // Emotion (pcode 5900004)
  HAPPY: { code: 5900004001, color: '#D20540' }, // 기쁨
  DEPRESSED: { code: 5900004002, color: '#30547D' }, // 우울
  WHEN_ANXIOUS: { code: 5900004003, color: '#756CF9' }, // 불안/초조
  IRRITATION_ANGER: { code: 5900004004, color: '#ED3B00' }, // 짜증/화
  STRESS: { code: 5900004005, color: '#1E1C5D' }, // 스트레스
  NOT_APPLICABLE: { code: 5900004999, color: '#B2BDCF' }, // 해당없음
};

// 복용약 정보 (DB 데이터 기반)
export const DRUG_INFO = {
  NICOCHAMPS: {
    medicineNm: '니코챔스',
    drugInfoSid: 1,
  },
  NICOPION: {
    medicineNm: '니코피온',
    drugInfoSid: 2,
  },
  NONE: {
    medicineNm: '없음',
    drugInfoSid: 3,
  },
};

// chatbot piechart color
export const CHATBOT_CHART_COLOR = [
  {
    code: '5900001001',
    color: '#1E1C5D',
  },
  {
    code: '5900001002',
    color: '#3831A1',
  },
  {
    code: '5900001003',
    color: '#554AEE',
  },
  {
    code: '5900001004',
    color: '#756CF9',
  },
  {
    code: '5900001005',
    color: '#034387',
  },
  {
    code: '5900002001',
    color: '#05529F',
  },
  {
    code: '5900002002',
    color: '#0468C8',
  },
  {
    code: '5900002004',
    color: '#2E96F9',
  },
  {
    code: '5900002003',
    color: '#1A808C',
  },
  {
    code: '5900004001',
    color: '#20A4B1',
  },
  {
    code: '5900004002',
    color: '#2FB49F',
  },
  {
    code: '5900003003',
    color: '#FF643C',
  },
  {
    code: '5900003001',
    color: '#F9CA32',
  },
  {
    code: '5900003002',
    color: '#FFA127',
  },
  {
    code: '5900001006',
    color: '#D20540',
  },
  {
    code: '5900003004',
    color: '#FD4583',
  },
  {
    code: '1',
    color: '#FFDC1B',
  },
  {
    code: '2',
    color: '#26A4F4',
  },
  {
    code: '3',
    color: '#FC8F59',
  },
  {
    code: '4',
    color: '#32BDAD',
  },
  {
    code: '5',
    color: '#4F7AFB',
  },
  {
    code: '6',
    color: '#FF7CAE',
  },
];

// 갈망 SOS 컨텐츠 타입 코드ID (TB_COMMON_CD CD_ID, pcode=5910)
export const CRAVING_SOS_CONTS_TYPE = {
  MEDITATION: 5910001,
  MULTIPLICATION_TABLES: 5910002,
  DRINK_WATER: 5910003,
  GAME_2048: 5910004,
  DODGE_BUTT: 5910005,
  HIDDEN_OBJECT: 5910006,
} as const;

export const COMMON_CODE_PID = {
  CRAVING_TYPE_CD: 5900,
};

export const SERVICE_CONTROL_PAGE_PATH = {
  login: 'login',
  findId: 'find-id',
  resetPwd: 'reset-pwd',
  register: 'register',
  verification: 'verification',
  home: 'home',
  drJin: 'drJin',
  missionHistory: 'mission-history',
  missionHistoryDetail: 'mission-history-detail',
  missionDetail: 'mission-detail',
  craving: 'craving',
  cravingRecord: 'craving-record',
  calendar: 'calendar',
  myPage: 'my-page',
  quitSmokingDiary: 'quit-smoking-diary',
  cravingDiary: 'craving-diary',
  quitSmokingAim: 'quit-smoking-aim',
  editProfile: 'edit-profile',
  editMemberInfo: 'edit-member-info',
  termsSettings: 'termsSettings',
  notificationSettings: 'notification-settings',
  faq: 'faq',
  version: 'version',
  privacyPolicy: 'privacyPolicy',
  service: 'service',
  appGuide: 'app-guide',
  supporter: 'supporter',
  ftnd: 'ftnd',
  medicalQuestion: 'medical-question',
  drugQuestion: 'drug-question',
  medicineSettings: 'medicine-settings',
  languageSettings: 'language-settings',
  personaSettings: 'persona-settings',
};

export const LANGUAGE = {
  korean: 'ko',
  english: 'en',
  deutsch: 'de',
};
